/**
 * Maps nula's design tokens into mermaid `themeVariables` for the `base` theme,
 * so diagrams read as native document content (subtle/neutral — primary used
 * sparingly for emphasis). Token values are read live (see readToken in
 * render-mermaid) so the theme tracks light/dark automatically. No hardcoded
 * colors — every value comes from a token, with mermaid-safe fallbacks so we
 * never emit empty strings (which would render broken colors).
 */

export type TokenReader = (token: string) => string;

/** Theme variables we set; values are resolved hex/strings, never CSS vars. */
export interface MermaidThemeVariables {
  primaryColor: string;
  primaryBorderColor: string;
  primaryTextColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  lineColor: string;
  textColor: string;
  mainBkg: string;
  clusterBkg: string;
  clusterBorder: string;
  edgeLabelBackground: string;
  fontFamily: string;
  [key: string]: string;
}

export function buildMermaidTheme(readToken: TokenReader): MermaidThemeVariables {
  const t = (token: string, fallback: string) => readToken(token).trim() || fallback;

  const surface = t('--surface-container-lowest', '#ffffff');
  const surfaceAlt = t('--surface-container', '#ececec');
  const text = t('--on-surface', '#2f3331');
  const textMuted = t('--on-surface-variant', '#606068');
  const border = t('--outline-variant', '#b0adb8');
  const primary = t('--primary', '#5e6ad2');
  const font = t('--font-sans', 'Inter, sans-serif');

  return {
    // Nodes: neutral surface fill, muted border, body text.
    primaryColor: surface,
    primaryBorderColor: border,
    primaryTextColor: text,
    // Secondary/tertiary node variants — slightly alternate surface.
    secondaryColor: surfaceAlt,
    tertiaryColor: surfaceAlt,
    // Edges + general text.
    lineColor: textMuted,
    textColor: text,
    mainBkg: surface,
    // Subgraphs / clusters: alternate container surface + muted border.
    clusterBkg: surfaceAlt,
    clusterBorder: border,
    // Edge labels sit on the diagram background.
    edgeLabelBackground: surface,
    fontFamily: font,
    // Emphasis: primary for active/relation lines (the one place brand pops).
    primaryColorEmphasis: primary,
  };
}
